import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo } from 'react';
import styled from 'styled-components';
import { ChevronLeftIcon, ChevronRightIcon } from '@minuthq/meatball-ui-icons';
import { ButtonStyleReset } from '../button/button.js';
import { generatePaginationPagesArray, isDivider } from './pagination-utils.js';
import { Text } from '../text/text.js';
import { colors } from '../../tokens/colors/colors.js';
import '../../tokens/shadows/shadows.js';
import { spacing } from '../../tokens/spacing/spacing.js';
import '../../tokens/breakpoints/breakpoints.js';

function Pagination({ currentPage, totalPages, onPageChange, edgeCount = 1, siblingCount = 1, }) {
    const hasPreviousPage = currentPage > 1;
    const hasNextPage = currentPage < totalPages;
    const internalEdgeCount = Math.max(1, edgeCount);
    const internalSiblingCount = Math.max(1, siblingCount);
    const pageNumbersArray = useMemo(() => {
        if (totalPages <= 1) {
            return [];
        }
        return generatePaginationPagesArray({
            currentPage,
            totalPages,
            edgeCount: internalEdgeCount,
            siblingCount: internalSiblingCount,
        });
    }, [totalPages, currentPage, internalEdgeCount, internalSiblingCount]);
    function handlePageClick(page, event) {
        if (page !== currentPage) {
            onPageChange(page, event);
        }
    }
    function handlePrevClick(event) {
        if (hasPreviousPage) {
            onPageChange(Math.max(0, currentPage - 1), event);
        }
    }
    function handleNextClick(event) {
        if (hasNextPage) {
            onPageChange(Math.min(currentPage + 1, totalPages), event);
        }
    }
    if (pageNumbersArray.length <= 1) {
        return null;
    }
    return (jsxs(Wrapper, { children: [jsx(StyledButton, { onClick: handlePrevClick, disabled: !hasPreviousPage, children: jsx(ChevronLeftIcon, {}) }), pageNumbersArray.map((page) => {
                const isActive = page === currentPage;
                if (isDivider(page)) {
                    return jsx(DividerWrapper, { children: "..." }, page);
                }
                return (jsx(StyledButton, { onClick: (event) => handlePageClick(page, event), "$active": isActive, children: jsx(Text, { variant: isActive ? "subtitleS" : "bodyS", children: page }) }, page));
            }), jsx(StyledButton, { onClick: handleNextClick, disabled: !hasNextPage, children: jsx(ChevronRightIcon, {}) })] }));
}
const Wrapper = styled.div `
  display: flex;
  align-items: center;
  gap: ${spacing.L};
  white-space: nowrap;
`;
const StyledButton = styled.button `
  ${ButtonStyleReset}
  border: none;
  padding: 0;
  transition: none;

  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.textPrimary};
  background-color: ${({ $active }) => $active ? colors.neutral : "transparent"};

  & > svg {
    display: block;
    width: 16px;
    height: 16px;
  }

  &:hover {
    background-color: ${colors.neutral};
  }

  &:disabled {
    cursor: auto;
    background-color: transparent;
    color: ${colors.textInactive};
  }
`;
const DividerWrapper = styled.div `
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.textPrimary};
`;

export { Pagination };
